import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Calendar as CalendarIcon, MapPin, Home, PartyPopper, Briefcase } from 'lucide-react';
import { authFetch, API } from '../utils/api';

function AttendanceHistory({ user }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await authFetch(`${API}/time/history?limit=100`);
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="attendance-page">
        <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-6">Attendance History</h1>

        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
              <p className="text-foreground-muted">No attendance records yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm text-foreground-DEFAULT">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-foreground-DEFAULT">Clock In</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-foreground-DEFAULT">Clock Out</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-foreground-DEFAULT">Duration</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-foreground-DEFAULT">Location</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-foreground-DEFAULT">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => (
                    <tr
                      key={entry.entry_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`attendance-row-${entry.entry_id}`}
                    >
                      <td className="py-3 px-4">
                        <p className="font-medium text-foreground-DEFAULT">
                          {new Date(entry.clock_in).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-foreground-muted">
                        {new Date(entry.clock_in).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 text-foreground-muted">
                        {entry.clock_out
                          ? new Date(entry.clock_out).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </td>
                      <td className="py-3 px-4 font-medium text-foreground-DEFAULT">
                        {entry.duration ? `${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {entry.location ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {entry.location === 'onsite' ? (
                              <>
                                <MapPin className="w-3 h-3" />
                                Onsite
                              </>
                            ) : entry.location === 'wfh' ? (
                              <>
                                <Home className="w-3 h-3" />
                                WFH
                              </>
                            ) : entry.location === 'event' ? (
                              <>
                                <PartyPopper className="w-3 h-3" />
                                Event
                              </>
                            ) : entry.location === 'field' ? (
                              <>
                                <Briefcase className="w-3 h-3" />
                                Field
                              </>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-foreground-muted text-xs">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            entry.status === 'active'
                              ? 'bg-accent/10 text-accent'
                              : 'bg-slate-200 text-slate-700'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AttendanceHistory;